import { useEffect, useState } from "react";
import { ShoppingCart, Check, Info, Sparkles, Share2, Copy, ListChecks, Printer, Zap, HelpCircle, Trash2, X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { categoryIcon } from "@/lib/constants";
import { weekRange, todayStr } from "@/lib/dates";

const CAT_ORDER = ["fruits_legumes", "viandes", "poisson", "produits_frais", "epicerie", "sauces_condiments", "boulangerie", "surgele", "autre"];
export default function Shopping() {
  const [list, setList] = useState(null);
  const [excludePantry, setExcludePantry] = useState(true);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [includeChecked, setIncludeChecked] = useState(false);
  const [showShortcutHelp, setShowShortcutHelp] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [newItem, setNewItem] = useState("");

  const load = () => { api.get("/shopping-list/current").then((r) => setList(r.data)); };
  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const generate = async (scope) => {
    setLoading(true);
    let start, end;
    if (scope === "today") { start = todayStr(); end = todayStr(); }
    else { const w = weekRange(); start = w.start; end = w.end; }
    try {
      const res = await api.post("/shopping-list/generate", { start, end, exclude_pantry: excludePantry });
      setList(res.data);
      toast.success("Liste générée !");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ajoutez d'abord des repas au planning");
    } finally {
      setLoading(false);
    }
  };

  const toggle = async (item) => {
    const newChecked = !item.checked;
    setList((l) => ({ ...l, items: l.items.map((it) => (it.id === item.id ? { ...it, checked: newChecked } : it)) }));
    await api.patch("/shopping-list/item", { item_id: item.id, checked: newChecked });
  };

  const uncheckAll = async () => {
    setList((l) => ({ ...l, items: l.items.map((it) => ({ ...it, checked: false })) }));
    try { const res = await api.post("/shopping-list/uncheck-all"); setList(res.data); toast.success("Tout décoché"); }
    catch { toast.error("Impossible de décocher"); load(); }
  };

  const clearList = async () => {
    setClearOpen(false);
    try { await api.delete("/shopping-list/current"); setList({ id: "current", items: [], start: null, end: null }); toast.success("Liste vidée"); }
    catch { toast.error("Impossible de vider la liste"); }
  };

  const addCustomItem = async () => {
    const name = newItem.trim();
    if (!name) return;
    try { const res = await api.post("/shopping-list/add-item", { name }); setList(res.data); setNewItem(""); }
    catch { toast.error("Impossible d'ajouter l'article"); }
  };
  const removeItem = async (item) => {
    setList((l) => ({ ...l, items: l.items.filter((it) => it.id !== item.id) }));
    try { await api.post("/shopping-list/remove-item", { item_id: item.id }); }
    catch { toast.error("Suppression impossible"); load(); }
  };

  const items = list?.items || [];
  const todo = items.filter((i) => !i.checked);
  const done = items.filter((i) => i.checked);

  const grouped = CAT_ORDER
    .map((cat) => ({ cat, rayon: (todo.find((i) => i.category === cat) || {}).rayon || cat, items: todo.filter((i) => i.category === cat) }))
    .filter((g) => g.items.length > 0);

  const regenerate = async () => {
    if (!list?.start) return;
    const res = await api.post("/shopping-list/generate", { start: list.start, end: list.end, exclude_pantry: list.exclude_pantry ?? excludePantry });
    setList(res.data);
  };

  const groupItems = (arr) => CAT_ORDER
    .map((cat) => ({ cat, rayon: (arr.find((i) => i.category === cat) || {}).rayon || cat, items: arr.filter((i) => i.category === cat) }))
    .filter((g) => g.items.length > 0);
  const srcItems = () => (includeChecked ? items : todo);

  const buildPretty = () => {
    let text = "🛒 Ma liste de courses — À Table\n";
    groupItems(srcItems()).forEach((g) => {
      text += `\n${categoryIcon(g.cat)} ${g.rayon}\n`;
      g.items.forEach((it) => { text += `• ${it.name} — ${it.quantity_display}\n`; });
    });
    return text.trim();
  };
  // Format optimisé pour l'app Rappels : une ligne = une tâche
  const buildReminders = () => srcItems().map((it) => `${it.name} — ${it.quantity_display}`).join("\n");

  const doShare = async (text, title) => {
    try {
      if (navigator.share) { await navigator.share({ title, text }); return true; }
    } catch (e) { return false; /* annulé */ }
    try { await navigator.clipboard.writeText(text); return "copied"; } catch (e) { return false; }
  };
  const doCopy = async (text) => {
    try { await navigator.clipboard.writeText(text); return true; } catch (e) { return false; }
  };

  const exportShare = async () => {
    if (!srcItems().length) { toast.error("Rien à exporter"); return; }
    const r = await doShare(buildPretty(), "Ma liste de courses");
    if (r === "copied") toast.success("Liste copiée — collez-la où vous voulez");
    setExportOpen(false);
  };
  const exportCopy = async () => {
    if (!srcItems().length) { toast.error("Rien à exporter"); return; }
    (await doCopy(buildPretty())) ? toast.success("Liste copiée dans le presse-papier") : toast.error("Copie impossible");
    setExportOpen(false);
  };
  const exportReminders = async () => {
    if (!srcItems().length) { toast.error("Rien à exporter"); return; }
    const text = buildReminders();
    const r = await doShare(text, "Courses");
    if (r === "copied" || r === true) {
      toast.success("Ouvrez Rappels, créez un rappel puis collez : chaque ligne devient une tâche");
    } else if (r === false) {
      (await doCopy(text)) && toast.success("Copié — collez dans un nouveau rappel (Rappels)");
    }
    setExportOpen(false);
  };
  const exportShortcut = () => {
    if (!srcItems().length) { toast.error("Rien à exporter"); return; }
    const text = buildReminders();
    // Ouvre l'app Raccourcis et exécute le raccourci nommé "Courses À Table" avec la liste en entrée
    window.location.href = `shortcuts://run-shortcut?name=${encodeURIComponent("Courses À Table")}&input=text&text=${encodeURIComponent(text)}`;
    setTimeout(() => setExportOpen(false), 300);
  };
  const exportPrint = () => {
    if (!srcItems().length) { toast.error("Rien à exporter"); return; }
    const gs = groupItems(srcItems());
    const w = window.open("", "_blank");
    if (!w) { toast.error("Autorisez les fenêtres pop-up pour imprimer"); return; }
    const today = new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
    const sections = gs.map((g) => `
      <h2>${g.rayon}</h2>
      <ul>${g.items.map((it) => `<li><span class="box"></span><span class="name">${it.name}</span><span class="qty">${it.quantity_display}</span></li>`).join("")}</ul>
    `).join("");
    w.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Liste de courses — À Table</title>
      <style>
        *{box-sizing:border-box} body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#2b2b2b;margin:32px;}
        h1{font-size:26px;margin:0 0 2px} .date{color:#8C7B75;margin:0 0 20px;text-transform:capitalize}
        h2{font-size:13px;text-transform:uppercase;letter-spacing:.06em;color:#7a9b76;border-bottom:1px solid #eee;padding-bottom:4px;margin:18px 0 6px}
        ul{list-style:none;padding:0;margin:0} li{display:flex;align-items:center;gap:10px;padding:7px 0;font-size:15px}
        .box{width:16px;height:16px;border:2px solid #b9b0aa;border-radius:4px;flex:0 0 auto}
        .name{flex:1;text-transform:capitalize} .qty{color:#E05A47;font-weight:600}
        @media print{body{margin:14mm}}
      </style></head><body>
      <h1>🛒 Ma liste de courses</h1><p class="date">${today} · À Table</p>${sections}
      </body></html>`);
    w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch (e) {} }, 500);
    setExportOpen(false);
  };

  const Row = ({ item }) => (
    <div className="flex items-center gap-3 py-3" data-testid={`shop-item-${item.id}`}>
      <button onClick={() => toggle(item)} data-testid={`check-${item.id}`}
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 at-press ${item.checked ? "text-white" : ""}`}
        style={item.checked ? { background: "var(--at-sage)", borderColor: "var(--at-sage)" } : { borderColor: "var(--at-border)" }}>
        {item.checked && <Check size={16} />}
      </button>
      {item.custom ? (
        <div className="flex flex-1 items-center justify-between">
          <span className={`font-medium capitalize ${item.checked ? "line-through opacity-50" : ""}`}>{item.name}</span>
          <span className="flex items-center gap-2">
            {item.quantity_display && <span className={`text-sm font-semibold ${item.checked ? "opacity-50" : ""}`} style={{ color: "var(--at-brand)" }}>{item.quantity_display}</span>}
            <button onClick={() => removeItem(item)} data-testid={`remove-item-${item.id}`} style={{ color: "var(--at-muted)" }}><X size={15} /></button>
          </span>
        </div>
      ) : (
        <button onClick={() => setDetail(item)} className="flex flex-1 items-center justify-between text-left">
          <span className={`font-medium capitalize ${item.checked ? "line-through opacity-50" : ""}`}>{item.name}</span>
          <span className="flex items-center gap-1.5">
            <span className={`text-sm font-semibold ${item.checked ? "opacity-50" : ""}`} style={{ color: "var(--at-brand)" }}>{item.quantity_display}</span>
            {item.sources?.length > 1 && <Info size={13} style={{ color: "var(--at-muted)" }} />}
          </span>
        </button>
      )}
    </div>
  );

  return (
    <div className="px-5 pt-8" data-testid="shopping-page">
      <header className="flex items-start justify-between at-fade-up">
        <div>
          <h1 className="font-display text-3xl font-bold">Mes courses</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--at-text-2)" }}>Une seule liste, consolidée et triée par rayon.</p>
        </div>
        {items.length > 0 && (
          <button data-testid="export-list-btn" onClick={() => setExportOpen(true)}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white at-press at-shadow" style={{ background: "var(--at-sage)" }}>
            <Share2 size={20} />
          </button>
        )}
      </header>

      {/* Génération */}
      <div className="mt-5 rounded-3xl border border-black/5 bg-white p-4 at-shadow at-fade-up" style={{ animationDelay: "50ms" }}>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Masquer mes basiques</span>
          </div>
          <Switch checked={excludePantry} onCheckedChange={setExcludePantry} data-testid="exclude-pantry-switch" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Button data-testid="generate-today-btn" onClick={() => generate("today")} disabled={loading}
            className="h-12 rounded-2xl font-semibold text-white at-press" style={{ background: "var(--at-honey)" }}>Aujourd'hui</Button>
          <Button data-testid="generate-week-btn" onClick={() => generate("week")} disabled={loading}
            className="h-12 rounded-2xl font-semibold text-white at-press" style={{ background: "var(--at-brand)" }}>
            <Sparkles size={16} className="mr-1.5" /> Cette semaine
          </Button>
        </div>
      </div>

      {/* Ajouter un article libre */}
      <div className="mt-3 flex gap-2 at-fade-up" style={{ animationDelay: "70ms" }}>
        <Input data-testid="add-item-input" value={newItem} onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addCustomItem()} placeholder="Ajouter un article (ex : éponges, sacs poubelle…)"
          className="h-12 rounded-2xl border-black/10 bg-white" />
        <Button data-testid="add-item-btn" onClick={addCustomItem} className="h-12 shrink-0 rounded-2xl px-4 text-white at-press" style={{ background: "var(--at-sage)" }}>
          <Plus size={20} />
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="mt-16 flex flex-col items-center text-center" style={{ color: "var(--at-muted)" }}>
          <ShoppingCart size={44} className="mb-3" />
          <p className="font-medium">Votre liste est vide</p>
          <p className="text-sm">Générez-la depuis vos repas planifiés.</p>
        </div>
      ) : (
        <>
          {list?.start && (
            <p className="mt-4 text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>
              {todo.length} article{todo.length > 1 ? "s" : ""} à acheter · mise à jour auto avec votre semaine
            </p>
          )}
          <div className="mt-2 space-y-4">
            {grouped.map((g) => (
              <section key={g.cat} className="at-fade-up" data-testid={`rayon-${g.cat}`}>
                <h2 className="mb-1 flex items-center gap-2 text-sm font-bold uppercase tracking-wide" style={{ color: "var(--at-text-2)" }}>
                  <span>{categoryIcon(g.cat)}</span> {g.rayon}
                </h2>
                <div className="divide-y divide-black/5 rounded-2xl border border-black/5 bg-white px-4 at-shadow">
                  {g.items.map((it) => <Row key={it.id} item={it} />)}
                </div>
              </section>
            ))}
          </div>

          {done.length > 0 && (
            <section className="mt-6" data-testid="done-section">
              <div className="mb-1 flex items-center justify-between">
                <h2 className="text-sm font-bold uppercase tracking-wide" style={{ color: "var(--at-sage)" }}>Achetés ({done.length})</h2>
                <button data-testid="uncheck-all-btn" onClick={uncheckAll}
                  className="rounded-full px-3 py-1 text-xs font-semibold at-press" style={{ background: "var(--at-elevated)", color: "var(--at-text-2)" }}>
                  Tout décocher
                </button>
              </div>
              <div className="divide-y divide-black/5 rounded-2xl border border-black/5 bg-white px-4 opacity-80 at-shadow">
                {done.map((it) => <Row key={it.id} item={it} />)}
              </div>
            </section>
          )}

          <button data-testid="clear-list-btn" onClick={() => setClearOpen(true)}
            className="mt-8 flex w-full items-center justify-center gap-2 rounded-2xl border py-3 text-sm font-semibold at-press"
            style={{ borderColor: "#F3C6BD", color: "var(--at-brand)" }}>
            <Trash2 size={16} /> Vider la liste
          </button>
        </>
      )}

      {/* Confirmation vider la liste */}
      <Dialog open={clearOpen} onOpenChange={setClearOpen}>
        <DialogContent className="rounded-3xl" data-testid="clear-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Vider la liste ?</DialogTitle>
            <DialogDescription>Tous les articles seront supprimés. Vous pourrez la régénérer depuis vos repas planifiés.</DialogDescription>
          </DialogHeader>
          <div className="mt-2 flex gap-3">
            <Button variant="outline" onClick={() => setClearOpen(false)} data-testid="clear-cancel-btn" className="h-12 flex-1 rounded-2xl font-semibold">Annuler</Button>
            <Button onClick={clearList} data-testid="clear-confirm-btn" className="h-12 flex-1 rounded-2xl font-semibold text-white at-press" style={{ background: "var(--at-brand)" }}>Vider</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export / partage de la liste */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="rounded-3xl" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Exporter ma liste</DialogTitle>
            <DialogDescription className="sr-only">Partager, copier ou envoyer la liste de courses vers Rappels</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="mb-1 flex items-center justify-between rounded-2xl px-3 py-2" style={{ background: "var(--at-elevated)" }}>
              <span className="text-sm font-medium">Inclure les articles déjà cochés</span>
              <Switch checked={includeChecked} onCheckedChange={setIncludeChecked} data-testid="export-include-checked" />
            </div>
            <button data-testid="export-share-btn" onClick={exportShare}
              className="flex w-full items-center gap-3 rounded-2xl border border-black/5 bg-white p-4 text-left at-shadow at-press">
              <span className="flex h-10 w-10 items-center justify-center rounded-full text-white" style={{ background: "var(--at-sage)" }}><Share2 size={18} /></span>
              <span>
                <span className="block font-semibold">Partager</span>
                <span className="block text-xs" style={{ color: "var(--at-text-2)" }}>Vers Notes, Messages, WhatsApp… (triée par rayon)</span>
              </span>
            </button>
            <button data-testid="export-copy-btn" onClick={exportCopy}
              className="flex w-full items-center gap-3 rounded-2xl border border-black/5 bg-white p-4 text-left at-shadow at-press">
              <span className="flex h-10 w-10 items-center justify-center rounded-full text-white" style={{ background: "var(--at-honey)" }}><Copy size={18} /></span>
              <span>
                <span className="block font-semibold">Copier le texte</span>
                <span className="block text-xs" style={{ color: "var(--at-text-2)" }}>Pour coller où vous voulez</span>
              </span>
            </button>
            <button data-testid="export-print-btn" onClick={exportPrint}
              className="flex w-full items-center gap-3 rounded-2xl border border-black/5 bg-white p-4 text-left at-shadow at-press">
              <span className="flex h-10 w-10 items-center justify-center rounded-full text-white" style={{ background: "#5B7C99" }}><Printer size={18} /></span>
              <span>
                <span className="block font-semibold">Imprimer / PDF</span>
                <span className="block text-xs" style={{ color: "var(--at-text-2)" }}>Belle liste à cocher pour le frigo</span>
              </span>
            </button>
            <button data-testid="export-reminders-btn" onClick={exportReminders}
              className="flex w-full items-center gap-3 rounded-2xl border border-black/5 bg-white p-4 text-left at-shadow at-press">
              <span className="flex h-10 w-10 items-center justify-center rounded-full text-white" style={{ background: "var(--at-brand)" }}><ListChecks size={18} /></span>
              <span>
                <span className="block font-semibold">Rappels (copier-coller)</span>
                <span className="block text-xs" style={{ color: "var(--at-text-2)" }}>Chaque article devient une tâche à cocher</span>
              </span>
            </button>
            <button data-testid="export-shortcut-btn" onClick={exportShortcut}
              className="flex w-full items-center gap-3 rounded-2xl border border-black/5 bg-white p-4 text-left at-shadow at-press">
              <span className="flex h-10 w-10 items-center justify-center rounded-full text-white" style={{ background: "#111827" }}><Zap size={18} /></span>
              <span className="flex-1">
                <span className="flex items-center gap-1.5 font-semibold">
                  Rappels en 1 tap
                  <span role="button" data-testid="export-shortcut-help" onClick={(e) => { e.stopPropagation(); setShowShortcutHelp((v) => !v); }} style={{ color: "var(--at-muted)" }}><HelpCircle size={14} /></span>
                </span>
                <span className="block text-xs" style={{ color: "var(--at-text-2)" }}>Via un raccourci Apple « Courses À Table »</span>
              </span>
            </button>
            {showShortcutHelp && (
              <div className="rounded-2xl p-3 text-xs leading-relaxed" style={{ background: "var(--at-elevated)", color: "var(--at-text-2)" }} data-testid="shortcut-help-box">
                <p className="mb-1 font-semibold" style={{ color: "var(--at-text)" }}>Configuration (une seule fois) :</p>
                1. Ouvrez l'app <b>Raccourcis</b> → <b>＋</b> nouveau raccourci.<br />
                2. Nommez-le exactement <b>« Courses À Table »</b>.<br />
                3. Ajoutez l'action <b>« Diviser le texte »</b> (séparateur : nouvelles lignes) sur l'<b>Entrée du raccourci</b>.<br />
                4. Ajoutez <b>« Répéter chaque élément »</b> → à l'intérieur, <b>« Ajouter un rappel »</b> avec l'élément.<br />
                Ensuite, ce bouton envoie votre liste au raccourci automatiquement.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Détail ingrédient */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-3xl" data-testid="ingredient-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl capitalize">{detail?.name} — {detail?.quantity_display}</DialogTitle>
            <DialogDescription className="sr-only">Détail du calcul de la quantité et équivalences</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>Détail du calcul</p>
            {detail?.sources?.map((s, i) => (
              <div key={i} className="flex items-center justify-between rounded-xl bg-[var(--at-elevated)] px-3 py-2">
                <span className="text-sm">{s.recipe_title}</span>
                <span className="text-sm font-semibold">{s.quantity !== null && s.quantity !== undefined ? `${s.quantity} ${s.unit || ""}`.trim() : (s.raw || "—")}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
